import React, { useState, useEffect, useRef } from 'react';
import { Container, Typography, TextField, Button, Grid, IconButton, Snackbar, Alert, Paper, Box, Autocomplete, CircularProgress, Collapse, Switch, FormControlLabel, Chip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { usePartyAutocomplete, useProductAutocomplete } from '../hooks/useAutocomplete';

const CreateOrder = () => {
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [enableDeliveryAssignment, setEnableDeliveryAssignment] = useState(false);
  const [order, setOrder] = useState({
    party: null,
    products: [{ productName: '', quantity: '', rate: '', assignedTo: null, description: '', showDescription: false, rateIncludesGST: false }]
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [validationErrors, setValidationErrors] = useState({});
  const [stockInfo, setStockInfo] = useState({}); // Store stock info for each product by index
  const navigate = useNavigate();

  // Autocomplete hooks
  const {
    options: partyOptions,
    loading: partyLoading,
    fetchSuggestions: fetchParties
  } = usePartyAutocomplete({ debounceMs: 300, minLength: 2 });

  const {
    options: productOptions,
    loading: productLoading,
    fetchSuggestions: fetchProducts
  } = useProductAutocomplete({ debounceMs: 300, minLength: 2 });

  // Refs for Enter key navigation
  const partyFieldRef = useRef(null);
  const productNameRefs = useRef([]);
  const quantityRefs = useRef([]);
  const rateRefs = useRef([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/');
          return;
        }

        // Only fetch feature flags (parties and products now use autocomplete)
        const featureFlagsResponse = await api.get('/config/features');

        const { enableDeliveryPersonAssignment } = featureFlagsResponse.data.features;
        setEnableDeliveryAssignment(enableDeliveryPersonAssignment);

        // Fetch delivery persons only if feature is enabled
        if (enableDeliveryPersonAssignment) {
          const deliveryPersonsResponse = await api.get('/users/delivery-persons');
          setDeliveryPersons(deliveryPersonsResponse.data);
        }
      } catch (error) {
        console.error('Error fetching data', error);
      }
    };
    fetchData();
  }, [navigate]);

  // Auto-focus party field on mount
  useEffect(() => {
    const focusPartyField = () => {
      if (partyFieldRef.current) {
        try {
          const input = partyFieldRef.current.querySelector('input');
          if (input) {
            input.focus();
          }
        } catch (error) {
          console.warn('Failed to focus party field:', error);
        }
      }
    };

    const timer = setTimeout(focusPartyField, 300);
    return () => clearTimeout(timer);
  }, []);

  // Enter key navigation handler
  const handleKeyDown = (e, fieldType, productIndex) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      switch (fieldType) {
        case 'party':
          // Focus first product's name field
          setTimeout(() => {
            try {
              if (productNameRefs.current[0]) {
                productNameRefs.current[0].focus();
              }
            } catch (error) {
              console.warn('Failed to focus product name field:', error);
            }
          }, 100);
          break;

        case 'productName':
          // Focus quantity field
          try {
            if (quantityRefs.current[productIndex]) {
              quantityRefs.current[productIndex].focus();
            }
          } catch (error) {
            console.warn('Failed to focus quantity field:', error);
          }
          break;

        case 'quantity':
          // Focus rate field
          try {
            if (rateRefs.current[productIndex]) {
              rateRefs.current[productIndex].focus();
            }
          } catch (error) {
            console.warn('Failed to focus rate field:', error);
          }
          break;

        case 'rate':
          // Validate and either add new product or focus submit
          const product = order.products[productIndex];
          if (product.productName && product.quantity && product.rate) {
            // Add new product and focus its name field
            addProduct();
            setTimeout(() => {
              try {
                if (productNameRefs.current[productIndex + 1]) {
                  productNameRefs.current[productIndex + 1].focus();
                }
              } catch (error) {
                console.warn('Failed to focus next product field:', error);
              }
            }, 150);
          }
          break;

        default:
          break;
      }
    }
  };

  const handlePartyChange = (event, value) => {
    setOrder({
      ...order,
      party: value
    });
    setErrors({ ...errors, party: null });
  };

  // Fetch stock info for a product and update rateIncludesGST
  const fetchStockInfo = async (productName, index) => {
    try {
      const response = await api.get(`/products/stock-info?productName=${encodeURIComponent(productName)}`);
      setStockInfo(prev => ({
        ...prev,
        [index]: response.data
      }));

      // Update product's rateIncludesGST from stock info response
      setOrder(prev => {
        const updatedProducts = [...prev.products];
        if (updatedProducts[index]) {
          updatedProducts[index] = {
            ...updatedProducts[index],
            rateIncludesGST: response.data.rateIncludesGST || false
          };
        }
        return { ...prev, products: updatedProducts };
      });
    } catch (error) {
      console.error('Error fetching stock info:', error);
      setStockInfo(prev => ({
        ...prev,
        [index]: null
      }));
    }
  };

  const handleProductChange = async (index, field, value, event = null) => {
    const updatedProducts = [...order.products];

    if (field === 'productName') {
      // value is the selected product object from Autocomplete
      updatedProducts[index].productName = value ? value.productName : '';

      // Copy all product properties if available
      if (value) {
        updatedProducts[index] = {
          ...updatedProducts[index],
          productName: value.productName,
          rate: value.rate || updatedProducts[index].rate,
          rateIncludesGST: value.rateIncludesGST || false
        };
      }

      setOrder({ ...order, products: updatedProducts });

      // Fetch stock info
      if (value && value.productName) {
        await fetchStockInfo(value.productName, index);
      } else {
        setStockInfo(prev => {
          const newInfo = { ...prev };
          delete newInfo[index];
          return newInfo;
        });
      }

      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`productName-${index}`];
        return newErrors;
      });
      return;
    }

    if (field === 'quantity' || field === 'rate') {
        // Allow decimal numbers with up to 2 decimal places
        const regex = /^\d*\.?\d{0,2}$/;

        if (value === '' || regex.test(value)) {
            updatedProducts[index][field] = value;

            if (value !== '') {
                const numValue = parseFloat(value);
                if (isNaN(numValue) || numValue <= 0) {
                    setValidationErrors(prev => ({
                        ...prev,
                        [`${field}-${index}`]: `${field === 'quantity' ? 'Quantity' : 'Rate'} must be greater than 0`
                    }));
                } else {
                    setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[`${field}-${index}`];
                        return newErrors;
                    });

                    // Validate quantity against stock if product is selected
                    if (field === 'quantity' && updatedProducts[index].productName && stockInfo[index]) {
                        const stock = stockInfo[index];
                        const quantity = numValue;

                        if (quantity > stock.netCurrent) {
                            setValidationErrors(prev => ({
                                ...prev,
                                [`quantity-${index}`]: `Quantity exceeds current stock (${stock.netCurrent})`
                            }));
                        } else if (quantity > stock.netAvailable) {
                            setValidationErrors(prev => ({
                                ...prev,
                                [`quantity-${index}`]: `Warning: Quantity exceeds available stock (${stock.netAvailable}). Reserved: ${stock.netReserved}`
                            }));
                        }
                    }
                }
            } else {
                // Clear errors when field is empty
                setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[`${field}-${index}`];
                    return newErrors;
                });
            }
        }
    } else if (field === 'productName' && value) {
        updatedProducts[index] = {
            ...updatedProducts[index],
            productName: value.productName
        };

        // Fetch stock info for the selected product
        await fetchStockInfo(value.productName, index);

        setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors['productName' + index];
            delete newErrors['quantity' + index]; // Clear quantity errors when product changes
            return newErrors;
        });

        // Re-validate quantity if it exists - use stockInfo from state instead of duplicate fetch
        // The stock info was already fetched above (line 214), so we'll validate after state updates
    } else if (field === 'assignedTo') {
        updatedProducts[index].assignedTo = value ? value._id : null;
    }

    setOrder({ ...order, products: updatedProducts });
  };

  const addProduct = () => {
    setOrder({
      ...order,
      products: [...order.products, { productName: '', quantity: '', rate: '', assignedTo: null, description: '', showDescription: false, rateIncludesGST: false }]
    });
  };

  const toggleDescription = (index) => {
    const updatedProducts = [...order.products];
    updatedProducts[index].showDescription = !updatedProducts[index].showDescription;
    setOrder({ ...order, products: updatedProducts });
  };

  const handleDescriptionChange = (index, value) => {
    const updatedProducts = [...order.products];
    updatedProducts[index].description = value;
    setOrder({ ...order, products: updatedProducts });
  };

  const handleRateIncludesGSTChange = (index, checked) => {
    const updatedProducts = [...order.products];
    updatedProducts[index].rateIncludesGST = checked;
    setOrder({ ...order, products: updatedProducts });
  };

  const removeProduct = (index) => {
    const updatedProducts = order.products.filter((_, i) => i !== index);
    setOrder({ ...order, products: updatedProducts });
  };

  const validateForm = () => {
    let tempErrors = {};

    if (!order.party) {
        tempErrors.party = 'Please select a party';
    }

    order.products.forEach((product, index) => {
        if (!product.productName) {
            tempErrors[`productName-${index}`] = 'Product name is required';
        }
    });

    // Check for duplicate products (only for products that have names)
    const seenProducts = new Map();
    order.products.forEach((product, index) => {
        if (product.productName && product.productName.trim() !== '') {
            const name = product.productName.trim().toLowerCase();
            if (seenProducts.has(name)) {
                const firstIndex = seenProducts.get(name);
                tempErrors[`productName-${index}`] = `Duplicate: Same product added at row ${firstIndex + 1}`;
                tempErrors[`productName-${firstIndex}`] = `Duplicate: Same product added at row ${index + 1}`;
            } else {
                seenProducts.set(name, index);
            }
        }
    });

    order.products.forEach((product, index) => {

        const quantity = product.quantity ? parseFloat(product.quantity) : 0;
        if (!product.quantity || isNaN(quantity) || quantity <= 0) {
            tempErrors[`quantity-${index}`] = 'Quantity must be greater than 0';
        } else if (stockInfo[index]) {
            // Check against current stock (hard limit)
            if (quantity > stockInfo[index].netCurrent) {
                tempErrors[`quantity-${index}`] = `Quantity exceeds current stock (${stockInfo[index].netCurrent})`;
            }
        }

        const rate = product.rate ? parseFloat(product.rate) : 0;
        if (!product.rate || isNaN(rate) || rate <= 0) {
            tempErrors[`rate-${index}`] = 'Rate must be greater than 0';
        }
    });

    setValidationErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const formatOrderData = () => {
    return {
        party: {
            _id: order.party._id,
            firmName: order.party.firmName,
            firmAddress: order.party.firmAddress,
            firmCityState: order.party.firmCityState,
            contactPerson: order.party.contactPerson,
            phoneNumber: order.party.phoneNumber
        },
        products: order.products.map(product => ({
            productName: product.productName,
            description: product.description || '',
            quantity: parseFloat(product.quantity),
            rate: parseFloat(product.rate),
            rateIncludesGST: product.rateIncludesGST || false,
            assignedTo: product.assignedTo || null
        }))
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
        return;
    }

    setIsLoading(true);
    try {
        const formattedData = formatOrderData();
        console.log('Sending order data:', formattedData); // Debug log

        await api.post('/orders/create', formattedData);
        
        setSnackbar({
            open: true,
            message: 'Order Created Successfully',
            severity: 'success'
        });
        
        setOrder({
            party: null,
            products: [{
                productName: '',
                quantity: '',
                rate: '',
                assignedTo: null,
                description: '',
                showDescription: false,
                rateIncludesGST: false
            }]
        });
        setStockInfo({}); // Clear stock info on reset

        // Auto-focus party field for next order
        setTimeout(() => {
          if (partyFieldRef.current) {
            const input = partyFieldRef.current.querySelector('input');
            if (input) input.focus();
          }
        }, 500);
    } catch (error) {
        console.error('Order creation error:', error);
        console.error('Error response:', error.response?.data);
        
        const errorMessage = error.response?.data?.errors?.[0]?.msg 
            || error.response?.data?.message 
            || 'Error creating order';
            
        setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error'
        });
    } finally {
        setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'primary.main',
            mb: 0.5
          }}
        >
          Create Order
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create a new order for your customers
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Autocomplete
          ref={partyFieldRef}
          options={partyOptions || []}
          loading={partyLoading}
          value={order.party}
          onChange={handlePartyChange}
          onInputChange={(event, value, reason) => {
            if (reason === 'input') {
              fetchParties(value);
            }
          }}
          getOptionLabel={(option) => option?.firmName || ''}
          isOptionEqualToValue={(option, value) => option?._id === value?._id}
          freeSolo={false}
          clearOnBlur={true}
          selectOnFocus={true}
          autoHighlight={true}
          disablePortal
          filterOptions={(x) => x}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Party Name"
              required
              error={!!errors.party}
              helperText={errors.party || "Press Enter to continue"}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !partyLoading) {
                  handleKeyDown(e, 'party', 0);
                }
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {partyLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'off',
                'data-form-type': 'other',
                'data-lpignore': 'true',
              }}
            />
          )}
          sx={{ mb: 2 }}
        />

        {order.party && (
          <Paper elevation={2} sx={{ p: 2, mt: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Party Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">Address</Typography>
                <Typography variant="body1">{order.party.firmAddress}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">City & State</Typography>
                <Typography variant="body1">{order.party.firmCityState}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">Contact Person</Typography>
                <Typography variant="body1">{order.party.contactPerson}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">Phone Number</Typography>
                <Typography variant="body1">{order.party.phoneNumber}</Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 3.75, mb: 1 }}>
          Products
        </Typography>

        {order.products.map((product, index) => (
          <Box key={index} sx={{ mb: 2, p: 1.5, border: '1px solid #ddd', borderRadius: '4px' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={enableDeliveryAssignment ? 4 : 5}>
                <Autocomplete
                  options={productOptions || []}
                  loading={productLoading}
                  value={product.productName ? { productName: product.productName } : null}
                  onChange={(event, value) => handleProductChange(index, 'productName', value, event)}
                  onInputChange={(event, value, reason) => {
                    if (reason === 'input') {
                      fetchProducts(value);
                    }
                  }}
                  getOptionLabel={(option) => option?.productName || ''}
                  isOptionEqualToValue={(option, value) => option?.productName === value?.productName}
                  freeSolo={false}
                  clearOnBlur={true}
                  selectOnFocus={true}
                  autoHighlight={true}
                  disablePortal
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Product Name"
                      required
                      error={!!validationErrors[`productName-${index}`]}
                      helperText={validationErrors[`productName-${index}`] || "Press Enter to continue"}
                      inputRef={(el) => (productNameRefs.current[index] = el)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !productLoading) {
                          handleKeyDown(e, 'productName', index);
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {productLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: 'new-password',
                      }}
                    />
                  )}
                />

                {/* Stock Info and Product Details Display */}
                {product.productName && stockInfo[index] && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Stock: Current: <strong>{stockInfo[index].netCurrent}</strong>,
                      Reserved: <strong style={{ color: '#ed6c02' }}>{stockInfo[index].netReserved}</strong>,
                      Available: <strong style={{ color: stockInfo[index].netAvailable > 0 ? '#2e7d32' : '#d32f2f' }}>
                        {stockInfo[index].netAvailable}
                      </strong>
                      {stockInfo[index].preferredLocation && (
                        <> | Location: <Chip label={stockInfo[index].preferredLocation} size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.7rem' }} /></>
                      )}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={product.rateIncludesGST || false}
                            onChange={(e) => handleRateIncludesGSTChange(index, e.target.checked)}
                            size="small"
                          />
                        }
                        label={<Typography variant="caption">Rate includes GST</Typography>}
                        sx={{ ml: 0 }}
                      />
                    </Box>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={enableDeliveryAssignment ? 7 : 6}>
                  <Grid container spacing={2} alignItems="center">
                      <Grid item xs={4} sm={enableDeliveryAssignment ? 3 : 5}>
                          <TextField
                              inputRef={(el) => (quantityRefs.current[index] = el)}
                              fullWidth
                              label="Qty/Wt"
                              value={product.quantity}
                              onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, 'quantity', index)}
                              variant="outlined"
                              required
                              error={!!validationErrors[`quantity-${index}`] && (
                                !validationErrors[`quantity-${index}`].includes('Warning') ||
                                validationErrors[`quantity-${index}`].includes('exceeds current stock')
                              )}
                              helperText={validationErrors[`quantity-${index}`] || "Enter to Move Ahead"}
                              InputProps={{ inputProps: { inputMode: 'decimal', pattern: '^[0-9]*\\.?[0-9]*$', placeholder: '1', step: 'any' } }}
                              slotProps={{
                                formHelperText: {
                                  sx: {
                                    color: validationErrors[`quantity-${index}`]?.includes('Warning') &&
                                           !validationErrors[`quantity-${index}`]?.includes('exceeds current stock')
                                      ? '#ed6c02'
                                      : undefined
                                  }
                                }
                              }}
                          />
                      </Grid>
                      <Grid item xs={6} sm={enableDeliveryAssignment ? 4 : 7}>
                          <TextField
                              inputRef={(el) => (rateRefs.current[index] = el)}
                              fullWidth
                              label="Rate"
                              value={product.rate}
                              onChange={(e) => handleProductChange(index, 'rate', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, 'rate', index)}
                              variant="outlined"
                              required
                              error={!!validationErrors[`rate-${index}`]}
                              helperText={validationErrors[`rate-${index}`] || "Enter to Add Product"}
                              InputProps={{ inputProps: { inputMode: 'decimal', pattern: '^[0-9]*\\.?[0-9]*$', placeholder: '0.00', step: 'any' } }}
                          />
                      </Grid>
                      {/* Mobile: Delete + Description toggle buttons */}
                      <Grid item xs={2} sm="auto" sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        <IconButton onClick={() => removeProduct(index)} disabled={order.products.length === 1} size="small">
                          <DeleteIcon color="error" />
                        </IconButton>
                        <IconButton
                          onClick={() => toggleDescription(index)}
                          size="small"
                          color={product.showDescription || product.description ? 'primary' : 'default'}
                          title={product.showDescription ? 'Hide description' : 'Add description'}
                        >
                          {product.showDescription ? <RemoveIcon /> : <AddIcon />}
                        </IconButton>
                      </Grid>
                      {enableDeliveryAssignment && (
                          <Grid item xs={12} sm={5}>
                              <Autocomplete
                                  options={deliveryPersons || []}
                                  getOptionLabel={(option) => option.name}
                                  renderInput={(params) => <TextField {...params} label="Assigned To" variant="outlined" />}
                                  value={(deliveryPersons || []).find(dp => dp._id === product.assignedTo) || null}
                                  onChange={(event, value) => handleProductChange(index, 'assignedTo', value)}
                                  fullWidth
                                  filterOptions={(options, state) => options.filter(option => option.name.toLowerCase().includes(state.inputValue.toLowerCase())).slice(0, 5)}
                              />
                          </Grid>
                      )}
                  </Grid>
              </Grid>

              {/* Desktop: Delete + Description toggle buttons */}
              <Grid item xs="auto" sm={1} sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <IconButton onClick={() => removeProduct(index)} disabled={order.products.length === 1}>
                  <DeleteIcon color="error" />
                </IconButton>
                <IconButton
                  onClick={() => toggleDescription(index)}
                  size="small"
                  color={product.showDescription || product.description ? 'primary' : 'default'}
                  title={product.showDescription ? 'Hide description' : 'Add description'}
                >
                  {product.showDescription ? <RemoveIcon /> : <AddIcon />}
                </IconButton>
              </Grid>
            </Grid>

            {/* Collapsible Description Field */}
            <Collapse in={product.showDescription}>
              <Box sx={{ mt: 2, pt: 1, borderTop: '1px dashed #ddd' }}>
                <TextField
                  fullWidth
                  label="Product Description (Optional)"
                  placeholder="Add any special notes or details for this product..."
                  value={product.description}
                  onChange={(e) => handleDescriptionChange(index, e.target.value)}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                />
              </Box>
            </Collapse>
          </Box>
        ))}

        <Button
          onClick={addProduct}
          variant="outlined"
          sx={{ mt: 1.25, fontWeight: 'bold' }}
        >
          Add Product
        </Button>

        {errors.products && <Typography color="error">{errors.products}</Typography>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2.5 }}>
            <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading}
                sx={{
                    fontWeight: 'bold',
                    mt: 2.5,
                    width: { xs: '100%', sm: 'auto' }, // Full width on mobile, auto on larger screens
                    py: 1.5 // A bit more padding for a better look
                }}
            >
                {isLoading ? 'Creating Order...' : 'Create Order'}
            </Button>
        </Box>
      </form>
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateOrder;